import { Injectable, inject } from '@angular/core';
import { Industry, IndustryService } from '@franbase/shared/data-access';
import { ComponentStore } from '@ngrx/component-store';
import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { EMPTY, Observable, catchError, combineLatest, distinctUntilChanged, map, mergeMap, of, switchMap, tap } from 'rxjs';

interface IndustryState extends EntityState<Industry> {
  loading: boolean,
  error: string,
  current_industry: Industry | null,
  nbHits: number,
  page: number,
  hitsPerPage: number
}
const adapter = createEntityAdapter<any>();
const initialState: IndustryState = adapter.getInitialState({
  loading: false,
  error: '',
  current_industry: null,
  nbHits: 0,
  page: 0,
  hitsPerPage: 0
})

@Injectable()
export class IndustryStoreService extends ComponentStore<IndustryState> {

  IndustrySV = inject(IndustryService);

  constructor() {
    super(initialState);
  }

  readonly data$ = this.select(s => Object.values(s.entities) as Industry[]);
  readonly loading$ = this.select(s => s.loading);
  readonly error$ = this.select((s) => s.error);
  private readonly nbHits$ = this.select((s) => s.nbHits);
  private readonly hitsPerPage$ = this.select((s) => s.hitsPerPage);
  private readonly page$ = this.select((s) => s.page);
  readonly current_industry$ = this.select((s) => s.current_industry);
  readonly vm$ = this.select(
    this.data$,
    this.nbHits$,
    this.hitsPerPage$,
    this.page$,
    (data, nbHits, hitsPerPage, page) => ({
      data,
      nbHits,
      hitsPerPage,
      page
    })
  );
  loadIndustry$ = this.effect((params$: Observable<any>) => {
    return params$.pipe(
      tap(() => {
        this.patchState({ loading: true, error: '' });
        this.setState(s => adapter.removeAll(s));
      }),
      switchMap((params: any) => {
        return this.IndustrySV.searchIndustryAlgolia(params).pipe(
          map((result: any) => {
            console.log(result)
            if (result.success) {
              let data = result.data.hits as Industry[];
              data = data.map(item => {
                item.id = item.objectID;
                return item;
              })
              this.setState(s => adapter.setAll(data, s))
              this.patchState({ nbHits: result.data.nbHits, hitsPerPage: result.data.hitsPerPage, page: result.data.page, loading: false, error: '' });
            } else {
              this.patchState({ loading: false, error: 'error get industry filter api' });
            }
          })
        )
      }),
      catchError(err => {
        this.patchState({ loading: false, error: err as string });
        throw new Error('cannot get api!');
        return EMPTY;
      })
    )
  });

  createIndustry$ = this.effect((industry$: Observable<Industry>) => {
    return industry$.pipe(
      tap(() => this.patchState({ loading: true, error: '' })),
      mergeMap((industry: Industry) => {
        return this.IndustrySV.addIndustry(industry);
      }),
      map(rs => {
        if (rs.flag) {
          this.setState((state) => adapter.setOne(rs.data, state));
          this.patchState({ loading: false });
        } else {
          this.patchState({ loading: false, error: rs.message });
        }
      }),
      catchError(err => {
        this.patchState({ loading: false, error: err as string });
        return EMPTY;
      })
    )
  });

  updateIndustry$ = this.effect((industry$: Observable<Industry>) => {
    return industry$.pipe(
      tap(() => this.patchState({ loading: true, error: '' })),
      mergeMap((industry: Industry) => {
        console.log('update project: ', industry);
        return combineLatest(this.IndustrySV.updateIndustry(industry), of(industry), this.current_industry$);
      }),
      map(([rs, industry, current_industry]) => {
        if (rs.flag) {
          this.setState((state) => adapter.updateOne({ id: industry.id!, changes: industry }, state));

          //update current project state
          if (current_industry && industry.id == current_industry.id) {
            this.setState((state) => ({ ...state, current_project_data: { ...state.current_industry, ...industry } }));
          }
          this.patchState({ loading: false });
        } else {
          this.patchState({ loading: false, error: rs.message });
        }
      }),
      catchError(err => {
        this.patchState({ loading: false, error: err as string });
        return EMPTY;
      })
    )
  });

  deleteIndustry$ = this.effect((industry$: Observable<Industry>) => {
    return industry$.pipe(
      tap(() => this.patchState({ loading: true, error: '' })),
      mergeMap((industry: Industry) => {
        return combineLatest(this.IndustrySV.deleteIndustry(industry.id!), of(industry));
      }),
      map(([rs, industry]) => {
        if (rs.flag) {
          this.setState((state) => adapter.removeOne(industry.id!, state));
          this.patchState({ loading: false });
        } else {
          console.log(rs);
          this.patchState({ loading: false, error: rs.message });
        }
      }),
      catchError(err => {
        console.log(err);
        this.patchState({ loading: false, error: err as string });
        return EMPTY;
      })
    )
  });

  getOneIndustry$ = this.effect((industry_id$: Observable<string>) => {
    return industry_id$.pipe(
      distinctUntilChanged(),
      mergeMap((industry_id) => {
        return this.IndustrySV.getIndustryDetail(industry_id).then(
          rs => {
            this.setState((state) => adapter.setOne(rs, state));
          }
        )
      })
    )
  })
  getOneIndustry(industry_id: string) {
    return this.select((s) => {
      return s.entities[industry_id] as Industry
    });
  }
  existsIndustryBySlug(slug: string) {
    return this.IndustrySV.existsIndustryBySlug(slug);
  }

}
